import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shape, bool, number, oneOf, func } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { ToggleButtons } from '@pwc/pwc-deals-ui';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { getFakeClientUser } from 'common/redux/security/loggedInUser/loggedInUserSlice';

import styles from './VersionToggleButtons.module.scss';
import featureFlags from '../../helpers/featureFlags';

const getText = ({ hideVersion, versionNumber = -1, versionName = '' }) => {
  if (hideVersion) {
    return versionName;
  }

  return versionNumber === -1
    ? versionName
    : `v${versionNumber} ${versionName}`;
};

const getCount = ({ commentCounts = {}, shared = false }) => {
  if (shared) {
    const { sharedVersion } = commentCounts || {};
    const { commentCount = 0 } = sharedVersion || {};

    return commentCount.toString();
  }

  const { draftVersion } = commentCounts || {};
  const { commentCount = 0 } = draftVersion || {};

  return commentCount.toString();
};

const VersionToggleButtons = ({
  config = {},
  isEntitySharedWithClient = false,
  mode = 'view',
  disabled = false,
  hideTextVersion = false,
  showSelected = true,
  redirectUrl,
  ...rest
}) => {
  const history = useHistory();
  const isFakeClient = useSelector(getFakeClientUser);
  const { t } = useTranslation();
  const {
    draftVersion,
    sharedVersion,
    commentCounts,
    draftOnClick,
    sharedOnClick,
  } = config || {};

  // eslint-disable-next-line
  const items = [];

  if (sharedVersion) {
    const { versionNumber } = sharedVersion || {};
    const content = t('clientDraftUnavailable');
    const popoverContent = <div>{content}</div>;
    const isCommentZero = getCount({ commentCounts, shared: true }) === '0';
    const sharedItemConfig = {
      text: getText({
        hideVersion: hideTextVersion,
        versionNumber,
        versionName: t('clientDraft'),
      }),
      disabled: mode === 'edit' && Object.keys(draftVersion || {}).length !== 0,
      onClick: sharedOnClick,
      popoverConfig:
        mode === 'edit'
          ? {
              content: popoverContent,
              placement: 'bottom',
            }
          : {},
    };
    if (versionNumber && isCommentZero) {
      items.push({
        className: 'no__comment version-toggle__item-shared',
        ...sharedItemConfig,
      });
    } else if (versionNumber && !isCommentZero) {
      items.push({
        className: 'version-toggle__item-shared',
        text: getText({
          hideVersion: hideTextVersion,
          versionNumber,
          versionName: t('clientDraft'),
        }),
        disabled: mode === 'edit' && draftVersion,
        onClick: sharedOnClick,
        iconConfig: {
          name: 'comment-bubble',
          text: getCount({ commentCounts, shared: true }),
          width: '20',
        },
        ...sharedItemConfig,
      });
    }
  }

  if (draftVersion) {
    const { versionNumber } = draftVersion || {};
    const isCommentZero = getCount({ commentCounts, shared: false }) === '0';
    const draftItemConfig = {
      text: getText({
        hideVersion: hideTextVersion,
        versionNumber,
        versionName: t('inProgress'),
      }),
      disabled: false,
      onClick: draftOnClick,
    };
    if (versionNumber && isCommentZero) {
      items.push({
        className: 'no__comment version-toggle__item-draft',
        ...draftItemConfig,
      });
    } else if (versionNumber && !isCommentZero) {
      items.push({
        className: 'version-toggle__item-draft',
        iconConfig: featureFlags.isVersioningPhase1Enabled
          ? undefined
          : {
              name: 'comment-bubble',
              text: getCount({ commentCounts, shared: false }),
              width: '20',
            },
        ...draftItemConfig,
      });
    }
  }

  const selectedIndex = !isEntitySharedWithClient && items.length === 2 ? 1 : 0;

  const fakeClientClickShared = useMemo(() => {
    if (isFakeClient) {
      const activeItem = items[selectedIndex];
      const viewingInProgress =
        activeItem?.text?.toLowerCase()?.indexOf('in progress') !== -1;
      const result =
        sharedVersion &&
        !isEmpty(sharedVersion) &&
        sharedOnClick &&
        viewingInProgress;
      return result;
    }
    return false;
  }, [sharedVersion, sharedOnClick, items, selectedIndex, isFakeClient]);

  useEffect(() => {
    if (isFakeClient) {
      const activeItem = items[selectedIndex];
      const viewingInProgress =
        activeItem?.text?.toLowerCase()?.indexOf('in progress') !== -1;
      if (fakeClientClickShared) {
        sharedOnClick();
      } else if (redirectUrl && viewingInProgress) {
        history.push(redirectUrl);
      }
    }
  }, [
    sharedVersion,
    isFakeClient,
    sharedOnClick,
    history,
    redirectUrl,
    selectedIndex,
    items,
    fakeClientClickShared,
  ]);

  if ((sharedVersion && isFakeClient) || fakeClientClickShared) {
    return null;
  }

  if (items.length === 0) {
    return <div className="version-toggle--empty" />;
  }

  const toggleButtonsConfig = {
    items,
    className: classNames(styles['version-toggle'], {
      [styles.disabled]: disabled,
    }),
  };

  const hasMultiVersions = items.length > 1;

  return (
    <ToggleButtons
      data-multi-versions={hasMultiVersions}
      config={toggleButtonsConfig}
      selectedIndex={showSelected ? selectedIndex : undefined}
      {...rest}
    />
  );
};

VersionToggleButtons.propTypes = {
  config: shape({
    draftVersion: shape({
      versionNumber: number,
    }),
    sharedVersion: shape({
      versionNumber: number,
    }),
    commentCounts: shape({
      draftVersion: shape({
        commentCount: number,
      }),
      sharedVersion: shape({
        commentCount: number,
      }),
    }),
    draftOnClick: func,
    sharedOnClick: func,
  }),
  isEntitySharedWithClient: bool,
  mode: oneOf(['view', 'edit']),
};

VersionToggleButtons.defaultProps = {
  config: {},
  isEntitySharedWithClient: false,
  mode: 'view',
};

export default VersionToggleButtons;
